import { formatDateTimeToLocaleString, IsoLocale, Locales } from "@simplyk/common";

import { Translate } from "@/common/hooks/useTranslate";

export type ScheduleSummaryTone = "neutral" | "warning" | "danger";

export interface ScheduleSummaryLabel {
  text: string;
  tone: ScheduleSummaryTone;
}

export interface BuildScheduleSummaryLabelInput {
  startDate: Date | string | null | undefined;
  endDate: Date | string | null | undefined;
  t: Translate;
  locale: IsoLocale | Locales;
  timezone: string;
  now: Date;
}

export const buildScheduleSummaryLabel = ({
  startDate,
  endDate,
  t,
  locale,
  timezone,
  now,
}: BuildScheduleSummaryLabelInput): ScheduleSummaryLabel | null => {
  if (!startDate && !endDate) {
    return null;
  }

  const dateFormat = { day: "numeric", month: "short" } as const;
  const formattedStartDate = startDate
    ? formatDateTimeToLocaleString(new Date(startDate), locale, dateFormat, timezone)
    : null;
  const formattedEndDate = endDate
    ? formatDateTimeToLocaleString(new Date(endDate), locale, dateFormat, timezone)
    : null;

  const hasEnded = Boolean(endDate) && new Date(endDate as Date | string).getTime() - now.getTime() <= 0;
  const isNotStarted = Boolean(startDate) && new Date(startDate as Date | string).getTime() > now.getTime();

  const text =
    formattedStartDate && formattedEndDate
      ? t("dashboard", "ticketing.extrasSummary.scheduleRange", {
          startDate: formattedStartDate,
          endDate: formattedEndDate,
        })
      : formattedStartDate
        ? t(
            "dashboard",
            isNotStarted ? "ticketing.extrasSummary.scheduleStarts" : "ticketing.extrasSummary.scheduleStarted",
            { date: formattedStartDate }
          )
        : t("dashboard", hasEnded ? "ticketing.extrasSummary.scheduleEnded" : "ticketing.extrasSummary.scheduleEnds", {
            date: formattedEndDate,
          });
  const tone: ScheduleSummaryTone = hasEnded ? "danger" : isNotStarted ? "warning" : "neutral";

  return { text, tone };
};
